import BaseService from "./BaseService";
import ModalComponent from "../components/ModalComponent";
import SliderShow from "../components/SliderShow/SliderShow";
import {Slide1, Slide2, Slide3, Slide4, Slide5, Slide6} from "../components/SliderShow/Slides";
import {OutOfMapComponent} from "../components/OutOfMapComponent";
import {Dialog} from "../components/Dialog";
import LanguageSelector from "../components/LanguageSelector";
import VisibilitySelector from "../components/VisibilitySelector";
import ReportErrorSelector from "../components/ReportErrorSelector";
import RateAppDialog from "../components/RateAppDialog";
import UpdateAppDialog from "../components/UpdateAppDialog";
import { AndroidSettings, IOSSettings, NativeSettings } from "capacitor-native-settings";
import AppService from "./AppService";
import MapService from "./MapService";
import i18n from "../i18n";
import type { ReactElement } from "react";

/** Datos opcionales que acompanan a la apertura de una modal. */
export interface ModalOptions {
    reportErrorEmailSubject?: string | null;
    reportErrorWhatsappMessage?: string | null;
    reportErrorCardTitle?: string | null;
}

export class ModalComponentService extends BaseService {

    static ModalComponentTypes = {
        INITIAL_CARD: 'INITIAL_CARD',
        OUT_OF_MAP_CARD: 'OUT_OF_MAP_CARD',
        DISCLAIMER_CARD: 'DISCLAIMER_CARD',
        LANGUAGE_SELECTOR_CARD: 'LANGUAGE_SELECTOR_CARD',
        VISIBILITY_SELECTOR_CARD: 'VISIBILITY_SELECTOR_CARD',
        PERMISSIONS_CARD: 'PERMISSIONS_CARD',
        REPORT_ERROR_CARD: 'REPORT_ERROR_CARD',
        RATE_APP_CARD: 'RATE_APP_CARD',
        UPDATE_APP_CARD: 'UPDATE_APP_CARD',
    } as const;

    static modalComponentType: ModalComponentType | null = null;
    static handleComponentChangeFunction: ModalChangeHandler;
    static dialogText = '';
    static reportErrorEmailSubject: string | null = null;
    static reportErrorWhatsappMessage: string | null = null;
    static reportErrorCardTitle: string | null = null;
    static shouldRestoreCurrentCard = false;

    // Opens a modal and stores optional contextual data for specific modal types.
    static openModalComponent(modalComponentType: ModalComponentType | null, options: ModalOptions = {}): void {
        this.shouldRestoreCurrentCard = Boolean(
            modalComponentType === this.ModalComponentTypes.REPORT_ERROR_CARD
            && MapService.mapComponent
            && MapService.mapComponent.state.showDetail
        );

        // Close any open card when opening a dialog (except when closing the modal)
        if (modalComponentType && MapService.mapComponent) {
            MapService.mapComponent.hideCurrentCard();
        }

        this.reportErrorEmailSubject = options.reportErrorEmailSubject || null;
        this.reportErrorWhatsappMessage = options.reportErrorWhatsappMessage || null;
        this.reportErrorCardTitle = options.reportErrorCardTitle || null;

        this.modalComponentType = modalComponentType;
        this.handleComponentChangeFunction(modalComponentType);
    }

    static setHandleComponentChangeFunction(changefunction: ModalChangeHandler): void {
        this.handleComponentChangeFunction = changefunction;
    }

    static getModalComponent(): ReactElement | null {
        switch (this.modalComponentType) {
            case this.ModalComponentTypes.INITIAL_CARD:
                return this.getInitialCard();
            case this.ModalComponentTypes.OUT_OF_MAP_CARD:
                return this.getOutOfMapCard();
            case this.ModalComponentTypes.DISCLAIMER_CARD:
                return this.getDisclaimerCard();
            case this.ModalComponentTypes.LANGUAGE_SELECTOR_CARD:
                return this.getLanguageSelectorCard();
            case this.ModalComponentTypes.VISIBILITY_SELECTOR_CARD:
                return this.getVisibilitySelectorCard();
            case this.ModalComponentTypes.PERMISSIONS_CARD:
                return this.getPermissionsCard();
            case this.ModalComponentTypes.REPORT_ERROR_CARD:
                return this.getReportErrorCard();
            case this.ModalComponentTypes.RATE_APP_CARD:
                return this.getRateAppCard();
            case this.ModalComponentTypes.UPDATE_APP_CARD:
                return this.getUpdateAppCard();
            default:
                return null;
        }
    }

    static reset(_event?: unknown, type?: ModalComponentType): void {
        if (type && type !== ModalComponentService.modalComponentType) {
            return;
        }
        const wasReportError = ModalComponentService.modalComponentType === ModalComponentService.ModalComponentTypes.REPORT_ERROR_CARD;
        const shouldRestoreCurrentCard = ModalComponentService.shouldRestoreCurrentCard;
        ModalComponentService.openModalComponent(null);
        if (wasReportError && shouldRestoreCurrentCard && MapService.mapComponent) {
            MapService.mapComponent.showCurrentCard();
        }
    }

    static getInitialCard(): ReactElement {
        return <ModalComponent
            modal={true}
            closeHandler={this.reset}
        >
            <SliderShow
                slides={[
                    <Slide5 key="slide5"/>,
                    <Slide1 key="slide1"/>,
                    <Slide2 key="slide2"/>,
                    <Slide3 key="slide3"/>,
                    <Slide4 key="slide4"/>,
                    <Slide6 key="slide6"/>,
                ]}
            />
        </ModalComponent>;
    }

    static getOutOfMapCard(): ReactElement {
        return <ModalComponent
            height="35vh"
            modal={false}
            position="top"
            closeHandler={this.reset}
        >
            <OutOfMapComponent></OutOfMapComponent>
        </ModalComponent>
    }

    static getDisclaimerCard(): ReactElement {
        return <ModalComponent
            modal={false}
            position="top"
            hideCloseIcon={true}
            autoHeight={true}
            customClass="disclaimer"
        >
            <Dialog
                closeHandler={this.reset}
                dialogText={this.dialogText}
            ></Dialog>
        </ModalComponent>;
    }

    static getLanguageSelectorCard(): ReactElement {
        return <ModalComponent
            modal={true}
            position="center"
            hideCloseIcon={false}
            autoHeight={false}
            height="250px"
            closeHandler={this.reset}
        >
            <LanguageSelector />
        </ModalComponent>
    }

    static getVisibilitySelectorCard(): ReactElement {
        return <ModalComponent
            modal={true}
            position="center"
            hideCloseIcon={false}
            autoHeight={false}
            height="250px"
            closeHandler={this.reset}
        >
            <VisibilitySelector />
        </ModalComponent>
    }

    static getPermissionsCard(): ReactElement {
        const onAccept = async () => {
            try {
                NativeSettings.open({
                    optionAndroid: AndroidSettings.ApplicationDetails, 
                    optionIOS: IOSSettings.App
                  })
            } finally {
                this.reset();
            }
        };

        const dialogText = AppService.isIos() ? i18n.t('geoPermission.messageIos') : i18n.t('geoPermission.messageAndroid');
    
        return <ModalComponent
            modal={true}
            position="center"
            hideCloseIcon={true}
            autoHeight={false}
            height="330px"
            closeHandler={this.reset}
        >
            <Dialog
                closeHandler={onAccept}
                cancelHandler={this.reset}
                dialogText={dialogText}
                acceptText={i18n.t('geoPermission.acceptText')}
                cancelText={i18n.t('geoPermission.cancelText')}
            />
        </ModalComponent>;
    }

    // Renders the report error modal content with temporary action buttons.
    static getReportErrorCard(): ReactElement {
        return <ModalComponent
            modal={true}
            position="center"
            hideCloseIcon={false}
            autoHeight={false}
            height="280px"
            closeHandler={this.reset}
        >
            <ReportErrorSelector
                reportErrorEmailSubject={this.reportErrorEmailSubject}
                reportErrorWhatsappMessage={this.reportErrorWhatsappMessage}
                reportErrorCardTitle={this.reportErrorCardTitle}
            />
        </ModalComponent>;
    }

    // Renders the rate app dialog asking the user to rate the app.
    static getRateAppCard(): ReactElement {
        return <ModalComponent
            modal={true}
            position="center"
            hideCloseIcon={true}
            autoHeight={false}
            height="250px"
            closeHandler={this.reset}
        >
            <RateAppDialog />
        </ModalComponent>;
    }

    // Renders the notice that a newer version has been published, with the
    // button that sends the user to the OS store.
    // @see App.noticeAppUpdate
    static getUpdateAppCard(): ReactElement {
        return <ModalComponent
            modal={true}
            position="center"
            hideCloseIcon={true}
            autoHeight={false}
            height="250px"
            closeHandler={this.reset}
        >
            <UpdateAppDialog />
        </ModalComponent>;
    }
}

/** Identificador de cada modal. */
export type ModalComponentType = typeof ModalComponentService.ModalComponentTypes[keyof typeof ModalComponentService.ModalComponentTypes];

/** Handler que registra App para que se repinte al cambiar la modal activa. */
export type ModalChangeHandler = (type: ModalComponentType | null) => void;
